/**
 * Calculateur de pente — page d'acquisition organique (anglais, marché US).
 *
 * Pourquoi cette page existe : « roof pitch calculator » et ses variantes
 * pèsent 22 200 recherches par mois aux États-Unis, en concurrence FAIBLE
 * (relevé du Planificateur Google, 2026-09-15), alors que « roof measurement
 * software » n'en pèse que 210. RoofSnap occupe déjà cette requête avec une
 * simple calculatrice. Le visiteur est un couvreur qui mesure un toit à
 * l'instant même : on lui rend le service gratuitement, puis on lui montre
 * qu'on peut faire les 14 pans à sa place.
 *
 * Volontairement en anglais seulement : la requête visée est américaine, et
 * une bascule de langue diluerait la page. Elle vit dans l'application React,
 * donc pixel, GA4 et bannière Loi 25 s'y appliquent comme ailleurs.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import ThemeSwitch from "@/components/ThemeSwitch";
import Marque from "@/components/Marque";
import SourcePreferee from "@/components/SourcePreferee";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone } from "lucide-react";

const CONTACT = "quotes@roofborder.com";
const TEL_AFFICHE = "1-866-4ROOF-21";
const TEL_LIEN = "tel:+18664766321";
const URL = "https://roofborder.com/roof-pitch-calculator";

// Le multiplicateur de pente est la seule vraie formule de la page :
// la surface d'un pan incliné vaut son empreinte fois racine(1 + (rise/run)²).
const multiplicateur = (rise, run) => Math.sqrt(1 + (rise / run) ** 2);
const degres = (rise, run) => (Math.atan(rise / run) * 180) / Math.PI;

const arrondi = (n, d = 2) =>
  Number.isFinite(n) ? n.toFixed(d).replace(/\.00$/, "") : "—";

// Table de référence 0/12 à 18/12 : c'est ce que les gens copient et ce qui
// fait revenir la page dans les résultats (« roof pitch multiplier », 1 300
// recherches par mois). Les valeurs sont recalculées, jamais recopiées.
const TABLE = Array.from({ length: 19 }, (_, rise) => ({
  rise,
  ratio: `${rise}/12`,
  angle: degres(rise, 12),
  pente: (rise / 12) * 100,
  mult: multiplicateur(rise, 12),
  nom:
    rise === 0
      ? "Flat"
      : rise <= 3
      ? "Low slope"
      : rise <= 6
      ? "Conventional"
      : rise <= 9
      ? "Steep"
      : "Very steep — steep-slope charges apply",
}));

const FAQ = [
  {
    q: "How do I calculate roof pitch?",
    r: "Roof pitch is the rise over a 12-inch run. Hold a level so 12 inches sit horizontally against the rafter or the roof surface, measure straight down from the free end of the level to the roof, and that vertical distance in inches is your rise. A rise of 6 inches over a 12-inch run is a 6/12 pitch.",
  },
  {
    q: "What is a roof pitch multiplier?",
    r: "The multiplier converts flat footprint area into actual sloped roof area. It equals the square root of 1 plus (rise ÷ run) squared. A 6/12 roof has a multiplier of 1.118, so 2,000 square feet of footprint is 2,236 square feet of roof.",
  },
  {
    q: "How do I measure roof pitch without climbing on the roof?",
    r: "Measure from inside the attic against a rafter with a level and a tape, use a pitch-finder app against a gable end, or let satellite imagery do it. Roof Border reads the pitch of each roof face from satellite data, so nobody goes up the ladder.",
  },
  {
    q: "What is the most common residential roof pitch?",
    r: "Most North American houses fall between 4/12 and 9/12. Anything under 3/12 is treated as low slope and usually needs a different underlayment; anything at 10/12 or above normally carries a steep-slope labor charge.",
  },
  {
    q: "How many degrees is a 6/12 roof pitch?",
    r: "A 6/12 pitch is 26.57 degrees, or a 50 percent slope. The calculator on this page converts any rise and run into degrees, percent and multiplier.",
  },
  {
    q: "How do I turn square feet into roofing squares?",
    r: "One roofing square is 100 square feet of roof surface — sloped area, not footprint. Divide the sloped area by 100, then add your waste factor. This calculator does both when you enter a footprint.",
  },
];

export default function CalculateurPente() {
  const [rise, setRise] = useState(6);
  const [run, setRun] = useState(12);
  const [empreinte, setEmpreinte] = useState("");
  const [perte, setPerte] = useState(10);

  const r = Number(rise) || 0;
  const c = Number(run) || 12;
  const mult = multiplicateur(r, c);
  const angle = degres(r, c);
  const pct = (r / c) * 100;

  const surface = Number(empreinte) > 0 ? Number(empreinte) * mult : null;
  const carres = surface ? (surface / 100) * (1 + (Number(perte) || 0) / 100) : null;

  const donnees = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.r },
          })),
        },
        {
          "@type": "WebApplication",
          name: "Roof Pitch Calculator",
          url: URL,
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          publisher: { "@type": "Organization", name: "Roof Border", url: "https://roofborder.com" },
        },
      ],
    }),
    []
  );

  useSeo({
    titre: "Roof Pitch Calculator — Degrees, Percent, Multiplier | Roof Border",
    description:
      "Free roof pitch calculator: turn rise and run into pitch, angle in degrees, percent slope, rafter length and the pitch multiplier. Includes a 0/12 to 18/12 chart and sloped-area conversion.",
    canonique: URL,
    donnees,
  });

  const champ =
    "w-full rounded-md border border-line-strong bg-field px-3 py-2 text-base text-ink focus:outline-none focus:ring-2 focus:ring-brand/40";

  return (
    <div className="min-h-screen bg-app text-ink">
      <header className="border-b border-line">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Marque taille="sm" />
          <div className="flex items-center gap-4">
            <ThemeSwitch />
            <Link to="/login" className="text-sm text-ink-soft hover:text-ink transition-colors">
              Sign in
            </Link>
            <Link to="/measure">
              <Button className="rounded-md h-9 px-4">Measure a roof</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <p className="text-xs font-bold tracking-widest uppercase text-brand-ink">Free tool</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">Roof Pitch Calculator</h1>
        <p className="mt-4 text-lg text-ink-soft max-w-2xl">
          Enter the rise and the run. You get the pitch, the angle in degrees, the percent slope,
          the rafter length and the pitch multiplier that turns footprint into real roof area.
          No sign-up, nothing to install.
        </p>

        <section className="mt-10 grid md:grid-cols-2 gap-6">
          <div className="rounded-xl border border-line p-6 bg-surface">
            <h2 className="text-lg font-bold">Your measurements</h2>

            <label className="block mt-5 text-sm font-medium" htmlFor="rise">
              Rise (inches)
            </label>
            <input
              id="rise"
              type="number"
              min="0"
              step="0.25"
              value={rise}
              onChange={(e) => setRise(e.target.value)}
              className={`mt-1 ${champ}`}
              data-testid="pente-rise"
            />

            <label className="block mt-4 text-sm font-medium" htmlFor="run">
              Run (inches) — 12 is the standard
            </label>
            <input
              id="run"
              type="number"
              min="1"
              step="1"
              value={run}
              onChange={(e) => setRun(e.target.value)}
              className={`mt-1 ${champ}`}
              data-testid="pente-run"
            />

            <label className="block mt-6 text-sm font-medium" htmlFor="empreinte">
              Roof footprint (sq ft) — optional
            </label>
            <input
              id="empreinte"
              type="number"
              min="0"
              step="10"
              placeholder="e.g. 2,000"
              value={empreinte}
              onChange={(e) => setEmpreinte(e.target.value)}
              className={`mt-1 ${champ}`}
              data-testid="pente-empreinte"
            />

            <label className="block mt-4 text-sm font-medium" htmlFor="perte">
              Waste factor (%)
            </label>
            <input
              id="perte"
              type="number"
              min="0"
              max="40"
              step="1"
              value={perte}
              onChange={(e) => setPerte(e.target.value)}
              className={`mt-1 ${champ}`}
              data-testid="pente-perte"
            />
          </div>

          <div className="rounded-xl border border-line p-6 bg-surface" data-testid="pente-resultat">
            <h2 className="text-lg font-bold">Result</h2>
            <dl className="mt-5 space-y-3 text-base">
              <div className="flex justify-between border-b border-line pb-2">
                <dt className="text-ink-soft">Pitch</dt>
                <dd className="font-bold">
                  {arrondi(r, 2)}/{arrondi(c, 0)}
                  {c !== 12 && <span className="text-ink-faint font-normal"> ({arrondi((r / c) * 12, 1)}/12)</span>}
                </dd>
              </div>
              <div className="flex justify-between border-b border-line pb-2">
                <dt className="text-ink-soft">Angle</dt>
                <dd className="font-bold">{arrondi(angle)}°</dd>
              </div>
              <div className="flex justify-between border-b border-line pb-2">
                <dt className="text-ink-soft">Slope</dt>
                <dd className="font-bold">{arrondi(pct, 1)} %</dd>
              </div>
              <div className="flex justify-between border-b border-line pb-2">
                <dt className="text-ink-soft">Pitch multiplier</dt>
                <dd className="font-bold">{arrondi(mult, 3)}</dd>
              </div>
              <div className="flex justify-between border-b border-line pb-2">
                <dt className="text-ink-soft">Rafter length per 12 in of run</dt>
                <dd className="font-bold">{arrondi(mult * 12, 2)} in</dd>
              </div>
              {surface && (
                <>
                  <div className="flex justify-between border-b border-line pb-2">
                    <dt className="text-ink-soft">Sloped roof area</dt>
                    <dd className="font-bold">{Math.round(surface).toLocaleString("en-US")} sq ft</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-ink-soft">Squares incl. {arrondi(Number(perte), 0)} % waste</dt>
                    <dd className="font-bold">{arrondi(carres, 1)} sq</dd>
                  </div>
                </>
              )}
            </dl>

            <div className="mt-6 rounded-lg bg-hover-strong p-4">
              <p className="text-sm text-ink-soft">
                This is one roof face. A real house has a dozen, each with its own pitch.
              </p>
              <Link to="/measure" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:underline">
                Measure every face from satellite <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <section className="mt-16">
          <h2 className="text-2xl font-extrabold tracking-tight">Roof pitch chart</h2>
          <p className="mt-2 text-ink-soft">
            Angle, percent slope and multiplier for every common pitch. Multiply the footprint of a
            roof face by the multiplier to get its real surface.
          </p>
          <div className="mt-5 overflow-x-auto rounded-xl border border-line">
            <table className="w-full text-sm">
              <thead className="bg-hover-strong text-left">
                <tr>
                  <th className="px-4 py-3 font-bold">Pitch</th>
                  <th className="px-4 py-3 font-bold">Angle</th>
                  <th className="px-4 py-3 font-bold">Slope</th>
                  <th className="px-4 py-3 font-bold">Multiplier</th>
                  <th className="px-4 py-3 font-bold">Category</th>
                </tr>
              </thead>
              <tbody>
                {TABLE.map((l) => (
                  <tr key={l.rise} className="border-t border-line">
                    <td className="px-4 py-2.5 font-medium">{l.ratio}</td>
                    <td className="px-4 py-2.5">{arrondi(l.angle)}°</td>
                    <td className="px-4 py-2.5">{arrondi(l.pente, 1)} %</td>
                    <td className="px-4 py-2.5">{arrondi(l.mult, 3)}</td>
                    <td className="px-4 py-2.5 text-ink-soft">{l.nom}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-16 max-w-3xl">
          <h2 className="text-2xl font-extrabold tracking-tight">How to measure roof pitch</h2>
          <ol className="mt-4 space-y-4 text-ink-soft list-decimal pl-5">
            <li>
              <span className="text-ink font-medium">From the attic.</span> Press one end of a
              2-foot level against the bottom of a rafter and hold it dead level. Mark 12 inches
              along the level, then measure straight up to the rafter. That distance is the rise.
            </li>
            <li>
              <span className="text-ink font-medium">From the gable end.</span> Same method on the
              outside of the gable, from a ladder. Safer than standing on the slope itself.
            </li>
            <li>
              <span className="text-ink font-medium">On the roof.</span> Lay the level on the
              shingles, level it, and measure down at the 12-inch mark. Only on a dry roof under
              6/12, with fall protection.
            </li>
            <li>
              <span className="text-ink font-medium">From satellite.</span> No ladder at all. Roof
              Border reads the pitch of each face from satellite data and gives you the area face
              by face, in about ten seconds.
            </li>
          </ol>
          <p className="mt-6 text-ink-soft">
            One caution that costs real money: a pitch of 10/12 is not a flat roof, and a flat roof
            is not a 3/12. Under 3/12 you are in low-slope territory and the underlayment changes;
            at 10/12 and above most crews add a steep-slope charge. Getting the pitch wrong by one
            category can move a quote by thousands of dollars.
          </p>
        </section>

        <section className="mt-16 max-w-3xl">
          <h2 className="text-2xl font-extrabold tracking-tight">Questions</h2>
          <div className="mt-5 space-y-5">
            {FAQ.map((f) => (
              <div key={f.q} className="border-t border-line pt-5">
                <h3 className="font-bold">{f.q}</h3>
                <p className="mt-2 text-ink-soft">{f.r}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 rounded-xl border border-line bg-surface p-8 text-center">
          <h2 className="text-2xl font-extrabold tracking-tight">Stop measuring one face at a time</h2>
          <p className="mt-3 text-ink-soft max-w-xl mx-auto">
            Put in an address. Roof Border returns every roof face, its pitch and its area from
            satellite imagery, then prices the job and builds a branded PDF quote.
          </p>
          <Link to="/measure">
            <Button className="mt-6 rounded-md h-12 px-7 text-base">
              Measure a roof <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </section>
      </main>

      <footer className="border-t border-line mt-10">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-ink-faint">
          <span>© {new Date().getFullYear()} Roof Border</span>
          <div className="flex items-center gap-4">
            <a href={TEL_LIEN} className="inline-flex items-center gap-1 hover:text-ink transition-colors">
              <Phone className="h-3.5 w-3.5" /> {TEL_AFFICHE}
            </a>
            <a href={`mailto:${CONTACT}`} className="hover:text-ink transition-colors">{CONTACT}</a>
            <Link to="/privacy" className="hover:text-ink transition-colors">Privacy</Link>
            <SourcePreferee lang="en" />
          </div>
        </div>
      </footer>
    </div>
  );
}
